/**
 * Timezone detection and smart defaults utilities
 */
import { getTimezoneOffset } from 'date-fns-tz';

/**
 * Detect user's browser timezone as IANA identifier
 */
export const detectUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Error detecting timezone:', error);
    return 'UTC'; // Safe fallback — never silently default to a specific timezone
  }
};

/**
 * Get user-friendly timezone name for display
 */
export const getTimezoneDisplayName = (timezone: string): string => {
  const timezoneNames: Record<string, string> = {
    'America/New_York': 'Eastern Time',
    'America/Chicago': 'Central Time', 
    'America/Denver': 'Mountain Time',
    'America/Los_Angeles': 'Pacific Time',
    'America/Anchorage': 'Alaska Time',
    'Pacific/Honolulu': 'Hawaii Time',
    'America/Halifax': 'Atlantic Time',
    'America/Sao_Paulo': 'Brazil Time',
    'Europe/London': 'Greenwich Mean Time',
    'Europe/Paris': 'Central European Time',
    'Europe/Athens': 'Eastern European Time',
    'Europe/Moscow': 'Moscow Time',
    'Asia/Dubai': 'Gulf Standard Time',
    'Asia/Karachi': 'Pakistan Standard Time',
    'Asia/Kolkata': 'India Standard Time',
    'Asia/Dhaka': 'Bangladesh Standard Time',
    'Asia/Bangkok': 'Indochina Time',
    'Asia/Shanghai': 'China Standard Time',
    'Asia/Tokyo': 'Japan Standard Time',
    'Australia/Sydney': 'Australian Eastern Time',
    'Pacific/Auckland': 'New Zealand Standard Time',
  };
  
  return timezoneNames[timezone] || timezone;
};

/**
 * Check if two timezones are significantly different (not same or adjacent)
 */
export const areTimezonesDifferent = (tz1: string | null, tz2: string): boolean => {
  if (!tz1) return true;
  
  if (tz1 === tz2) return false;
  
  // Check if they're in the same general region (e.g., US timezones)
  const usTimezones = [
    'America/New_York',
    'America/Chicago', 
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage'
  ];
  
  const euTimezones = [
    'Europe/London',
    'Europe/Paris',
    'Europe/Athens',
    'Europe/Moscow'
  ];
  
  const asiaTimezones = [
    'Asia/Dubai',
    'Asia/Karachi',
    'Asia/Kolkata',
    'Asia/Dhaka',
    'Asia/Bangkok',
    'Asia/Shanghai',
    'Asia/Tokyo'
  ];
  
  // If both are in same region, consider them not significantly different
  if ((usTimezones.includes(tz1) && usTimezones.includes(tz2)) ||
      (euTimezones.includes(tz1) && euTimezones.includes(tz2)) ||
      (asiaTimezones.includes(tz1) && asiaTimezones.includes(tz2))) {
    return false;
  }
  
  return true;
};

/**
 * Format timezone offset for display (e.g., "UTC-5" or "UTC+8")
 * Uses date-fns-tz for accurate daylight saving time handling
 */
export const formatTimezoneOffset = (timezone: string): string => {
  try {
    const now = new Date();
    // getTimezoneOffset from date-fns-tz returns milliseconds, convert to minutes
    const offsetMilliseconds = getTimezoneOffset(timezone, now);
    const offsetMinutes = Math.round(offsetMilliseconds / (1000 * 60));
    
    if (offsetMinutes === 0) return 'UTC±0';
    
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMinutesRemainder = Math.abs(offsetMinutes) % 60;
    
    // For timezone display: positive offset means ahead of UTC (UTC+), negative means behind (UTC-)
    // Eastern Time is UTC-4 (behind UTC), so we want negative offset to show minus sign
    const sign = offsetMinutes > 0 ? '+' : '-';
    
    if (offsetMinutesRemainder === 0) {
      return `UTC${sign}${offsetHours}`;
    } else {
      return `UTC${sign}${offsetHours}:${offsetMinutesRemainder.toString().padStart(2, '0')}`;
    }
  } catch (error) {
    console.error('Error formatting timezone offset:', error);
    return 'UTC';
  }
};

/**
 * Get timezone display with offset (e.g., "America/New_York (UTC-4)")
 */
export const getTimezoneDisplayWithOffset = (timezone: string): string => {
  try {
    const displayName = getTimezoneDisplayName(timezone);
    const offset = formatTimezoneOffset(timezone);
    return `${displayName} (${offset})`;
  } catch (error) {
    console.error('Error getting timezone display with offset:', error);
    return timezone;
  }
};

/**
 * Get compact timezone display with IANA and offset (e.g., "America/New_York (UTC-4)")
 */
export const getCompactTimezoneDisplay = (timezone: string): string => {
  try {
    const offset = formatTimezoneOffset(timezone);
    return `${timezone} (${offset})`;
  } catch (error) {
    console.error('Error getting compact timezone display:', error);
    return timezone;
  }
};

/**
 * Smart timezone suggestion based on user's detected timezone
 */
export const suggestUserTimezone = (detectedTimezone?: string): string => {
  const detected = detectedTimezone || detectUserTimezone();
  
  // Map some common variations to our supported timezones
  const mappings: Record<string, string> = {
    'America/Detroit': 'America/New_York',
    'America/Indianapolis': 'America/New_York',
    'America/Louisville': 'America/New_York',
    'America/Montreal': 'America/New_York',
    'America/Toronto': 'America/New_York',
    'America/Winnipeg': 'America/Chicago',
    'America/Phoenix': 'America/Denver',
    'America/Vancouver': 'America/Los_Angeles',
    'Europe/Belfast': 'Europe/London',
    'Europe/Dublin': 'Europe/London',
    'Europe/Amsterdam': 'Europe/Paris',
    'Europe/Berlin': 'Europe/Paris',
    'Europe/Brussels': 'Europe/Paris',
    'Europe/Madrid': 'Europe/Paris',
    'Europe/Rome': 'Europe/Paris',
    'Europe/Vienna': 'Europe/Paris',
    'Europe/Zurich': 'Europe/Paris',
  };
  
  return mappings[detected] || detected;
};